import {Link} from '@wandb/components';
import * as globals from '@wandb/weave/common/css/globals.styles';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox/Checkbox';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {memo, useCallback, useContext, useState} from 'react';

import {AccountSelectorContext} from '../../../components/Search/SearchNav/AccountSelectorContextProvider';
import {AccountPlanType} from '../../../components/Search/SearchNav/types';
import {
  useCreateInferenceSubscriptionMutation,
  useCreateStorageSubscriptionMutation,
  useMakeOrgMembersNoAccessModelsSeatMutation,
  useUpdateCustomerDefaultPaymentMethodMutation,
  useUpgradeTrialSubscriptionMutation,
} from '../../../generated/graphql';
import {captureError} from '../../../integrations';
import {useGetSubscriptionPlanByName} from '../../../util/accounts/pricing';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {privacyPolicy, termsOfService} from '../../../util/urls';
import {PlanName} from '../util';
import {NewStripeForm, type SubmitParams} from './NewStripeForm';

export type NewStorageSubscribeFormProps = {
  organizationID: string;
  hasStripePaymentMethods: boolean;
  onSubscriptionCompleted: () => void;
  isBuyingInference?: boolean;
};

type SubscribeToStorageMutationParams = {
  organizationId?: string;
  paymentMethod: string;
};

// TODO(hhuang): Also does inference, maybe pull it out later
const NewStorageSubscribeFormComp = ({
  organizationID,
  hasStripePaymentMethods,
  onSubscriptionCompleted,
  isBuyingInference = false,
}: NewStorageSubscribeFormProps) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const [createStorageSubscriptionMutation] =
    useCreateStorageSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [makeOrgMembersNoAccessModelsSeatMutation] =
    useMakeOrgMembersNoAccessModelsSeatMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [createInferenceSubscriptionMutation] =
    useCreateInferenceSubscriptionMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const {loading: plansLoading, plans} = useGetSubscriptionPlanByName();

  const communityEditionPlan = plans[PlanName.CommunityEdition];

  const [downgradeTrialSubscription] = useUpgradeTrialSubscriptionMutation({
    variables: {
      organizationID: organizationID,
      planID: communityEditionPlan?.id ?? '',
    },
  });

  const [updateDefaultPaymentMethodMutation] =
    useUpdateCustomerDefaultPaymentMethodMutation({
      context: {...propagateErrorsContext(), ...doNotRetryContext()},
    });

  const [isTermsChecked, setIsTermsChecked] = useState(false);

  const submit = useCallback(
    async ({
      stripe,
      custoEmail,
      paymentMethodID,
      setPaymentMethodAsDefault,
      setErrMsg,
    }: SubmitParams) => {
      const subscription = await subscribe();
      if (subscription == null) {
        return false;
      }

      // if the purchase was successful and the set payment method flag is true,
      // set the payment method as default
      if (
        setPaymentMethodAsDefault &&
        paymentMethodID != null &&
        organizationID != null &&
        organizationID !== ''
      ) {
        const {errors} = await updateDefaultPaymentMethodMutation({
          variables: {
            paymentMethod: paymentMethodID,
            organizationId: organizationID,
          },
        });

        // since the subscription itself was successful, just surface the error
        if (errors != null) {
          const errMsg = extractErrorMessageFromApolloError(errors);
          if (errMsg != null) {
            setErrMsg(errMsg);
          }
          return false;
        }
      }

      const paymentIntent = subscription.latest_invoice.payment_intent;
      const clientSecret = paymentIntent?.client_secret;
      const paymentIntentStatus = paymentIntent?.status;
      const needsPayment =
        ['requires_action', 'requires_payment_method'].indexOf(
          paymentIntentStatus
        ) !== -1;

      if (needsPayment) {
        const paymentConfirmed = await confirmPayment(clientSecret);
        if (!paymentConfirmed) {
          return false;
        }
      }

      onSubscriptionCompleted();
      return true;

      // HELPER FUNCTIONS

      async function subscribe(): Promise<{latest_invoice: any} | null> {
        if (paymentMethodID == null || plansLoading) {
          return null;
        }
        try {
          const variables: SubscribeToStorageMutationParams = {
            paymentMethod: paymentMethodID,
          };

          // if its a personal subscription, the org ID will be emtpy, so we don't pass it in
          if (organizationID != null && organizationID !== '') {
            variables.organizationId = organizationID;
          }

          if (
            isBuyingInference &&
            selectedAccount?.accountPlanType === AccountPlanType.ThirtyDayTrial
          ) {
            // fiiiiiiiine
            await makeOrgMembersNoAccessModelsSeatMutation({
              variables: {organizationId: organizationID},
            });
            await downgradeTrialSubscription();
          }

          // Use the appropriate mutation based on whether we're buying inference or storage
          const sub = isBuyingInference
            ? (
                await createInferenceSubscriptionMutation({
                  variables,
                })
              ).data?.createInferenceSubscription?.subscription
            : (
                await createStorageSubscriptionMutation({
                  variables,
                })
              ).data?.createStorageSubscription?.subscription;

          if (sub == null) {
            onError({
              err: "Unknown error confirming subscription. Exception didn't throw but success was not reported.",
              op: 'handleSubscribe-unknown',
              analyticsEvent: isBuyingInference
                ? 'Create Inference Subscription Error'
                : 'Create Storage Subscription Error',
              extra: {
                errorType: isBuyingInference
                  ? 'unknown error confirming inference subscription'
                  : 'unknown error confirming storage subscription',
              },
              userFacingErrorMsg:
                'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.',
            });
            return null;
          }

          return sub;
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err);
          onError({
            err,
            op: 'createSubResult',
            userFacingErrorMsg: isBuyingInference
              ? `Error processing inference subscription payment: ${errMsg}`
              : `Error processing storage subscription payment: ${errMsg}`,
            analyticsEvent: isBuyingInference
              ? 'Create Inference Subscription Error'
              : 'Create Storage Subscription Error',
            extra: {
              reason: errMsg,
              errorType: isBuyingInference
                ? 'processing inference subscription payment'
                : 'processing storage subscription payment',
            },
          });
          return null;
        }
      }

      async function confirmPayment(secret: string): Promise<boolean> {
        try {
          const result = await stripe.confirmCardPayment(secret);

          if (result.error) {
            onError({
              err: result.error.message!,
              op: 'handleCardPayment',
              analyticsEvent: isBuyingInference
                ? 'Create Inference Subscription Error'
                : 'Create Storage Subscription Error',
              extra: {
                errorType: isBuyingInference
                  ? 'confirming inference subscription payment'
                  : 'confirming storage subscription payment',
                reason: result.error.message,
              },
              userFacingErrorMsg: `We had trouble confirming your payment: ${result.error.message}`,
            });
            return false;
          }

          return true;
        } catch (err) {
          onError({
            err,
            op: 'handleCardPayment',
            extra: {
              errorType: isBuyingInference
                ? 'confirming inference subscription payment'
                : 'confirming storage subscription payment',
              reason: err,
            },
            analyticsEvent: isBuyingInference
              ? 'Create Inference Subscription Error'
              : 'Create Storage Subscription Error',
            userFacingErrorMsg: `We had trouble confirming your payment: ${err}`,
          });
          return false;
        }
      }

      type ErrOpts = {
        err: unknown;
        op: string;
        userFacingErrorMsg: string;
        analyticsEvent?: string;
        extra?: {[key: string]: any};
      };
      function onError(opts: ErrOpts) {
        const {err, op, userFacingErrorMsg, analyticsEvent} = opts;
        if (!(err instanceof Error) && typeof err !== 'string') {
          return;
        }

        const extra = {
          email: custoEmail,
          units: 1,
          trial: false,
          ...opts.extra,
        };
        captureError(err, `StorageSubscribeForm-${op}`, {
          extra,
        });
        if (analyticsEvent != null) {
          window.analytics?.track(analyticsEvent, extra);
        }
        setErrMsg(userFacingErrorMsg);
      }
    },
    [
      organizationID,
      onSubscriptionCompleted,
      updateDefaultPaymentMethodMutation,
      plansLoading,
      isBuyingInference,
      selectedAccount?.accountPlanType,
      createInferenceSubscriptionMutation,
      createStorageSubscriptionMutation,
      makeOrgMembersNoAccessModelsSeatMutation,
      downgradeTrialSubscription,
    ]
  );

  if (plansLoading) {
    return null;
  }

  return (
    <Tailwind>
      <NewStripeForm
        hideOrgName
        hasStripePaymentMethods={hasStripePaymentMethods}
        orgID={organizationID}
        renderButtons={({submitWrapper, submitButtonDisabled, submitting}) => (
          <div className="mt-4 flex w-full flex-col gap-4">
            <div className="-ml-12 flex flex-row items-center">
              <Checkbox
                style={{margin: 12}}
                checked={isTermsChecked}
                onClick={() => setIsTermsChecked(!isTermsChecked)}
              />
              <span className="text-14 text-gray-700">
                I agree to the{' '}
                <Link href={termsOfService()} target="_blank">
                  terms of service
                </Link>{' '}
                and{' '}
                <Link href={privacyPolicy()} target="_blank">
                  privacy policy
                </Link>
                .
              </span>
            </div>
            <Button
              className="w-full"
              size="large"
              color={globals.TEAL_500}
              onClick={() => {
                window.analytics?.track(
                  isBuyingInference
                    ? 'Inference subscribe purchase button clicked'
                    : 'Storage subscribe purchase button clicked'
                );
                submitWrapper(submit);
              }}
              disabled={submitButtonDisabled || !isTermsChecked}>
              {submitting
                ? 'Processing...'
                : isBuyingInference
                  ? 'Activate'
                  : 'Subscribe'}
            </Button>
          </div>
        )}
      />
    </Tailwind>
  );
};

export const NewStorageSubscribeForm = memo(NewStorageSubscribeFormComp);
